export type MosquitoNetType = 'roll' | 'fixed' | 'slide' | 'pleated'
export type MosquitoNetFixedSide = 'left' | 'right'

export type MosquitoNetConfiguration = {
  type: MosquitoNetType
  spring: boolean | null
  fixedSide: MosquitoNetFixedSide | null
  netColorBlack: boolean
}

export function emptyMosquitoNet(): MosquitoNetConfiguration {
  return { type: 'roll', spring: true, fixedSide: null, netColorBlack: false }
}

export function normalizeMosquitoNet(
  value?: Partial<MosquitoNetConfiguration> | null,
  current?: MosquitoNetConfiguration,
): MosquitoNetConfiguration {
  const source = { ...emptyMosquitoNet(), ...current, ...value }
  if (!['roll', 'fixed', 'slide', 'pleated'].includes(source.type)) throw new Error('Choose a valid mosquito-net type.')
  if (typeof source.netColorBlack !== 'boolean') throw new Error('Black mosquito-net color must be a boolean value.')
  if (source.type === 'roll') {
    if (typeof source.spring !== 'boolean') throw new Error('Choose whether the roll mosquito net includes a spring.')
    return { type: 'roll', spring: source.spring, fixedSide: null, netColorBlack: false }
  }
  if (source.type === 'slide') {
    if (!['left', 'right'].includes(source.fixedSide || '')) throw new Error('Choose the fixed side, viewed from inside.')
    return { type: 'slide', spring: null, fixedSide: source.fixedSide as MosquitoNetFixedSide, netColorBlack: source.netColorBlack }
  }
  if (source.type === 'pleated') {
    // Pleated mesh collects on one side.  When a window is changed from a
    // roll/fixed net (or a legacy record has no side), use the documented
    // left-hand default; an existing slide side is retained by the merged
    // `current` value above.
    const fixedSide = ['left', 'right'].includes(source.fixedSide || '')
      ? source.fixedSide as MosquitoNetFixedSide
      : 'left'
    return { type: 'pleated', spring: null, fixedSide, netColorBlack: false }
  }
  return { type: 'fixed', spring: null, fixedSide: null, netColorBlack: source.netColorBlack }
}

export function readMosquitoNet(record: Record<string, unknown>): MosquitoNetConfiguration {
  const type = ['roll', 'fixed', 'slide', 'pleated'].includes(String(record.mosquitoNetType))
    ? record.mosquitoNetType as MosquitoNetType
    : 'roll'
  return normalizeMosquitoNet({
    type,
    spring: type === 'roll' ? record.mosquitoNetSpring !== false : null,
    netColorBlack: record.mosquitoNetNetColorBlack === true,
    fixedSide: (type === 'slide' || type === 'pleated') && ['left', 'right'].includes(String(record.mosquitoNetFixedSide))
      ? record.mosquitoNetFixedSide as MosquitoNetFixedSide
      : type === 'slide' || type === 'pleated' ? 'left' : null,
  })
}

export function mosquitoNetFields(value?: MosquitoNetConfiguration) {
  if (!value) return { mosquitoNetType: null, mosquitoNetSpring: null, mosquitoNetFixedSide: null, mosquitoNetNetColorBlack: null }
  const net = value
  return {
    mosquitoNetType: net.type,
    mosquitoNetSpring: net.type === 'roll' ? net.spring : null,
    mosquitoNetFixedSide: net.type === 'slide' || net.type === 'pleated' ? net.fixedSide : null,
    mosquitoNetNetColorBlack: net.type === 'roll' || net.type === 'pleated' ? false : net.netColorBlack,
  }
}

export function mosquitoNetValid(value?: MosquitoNetConfiguration) {
  try { normalizeMosquitoNet(value); return true } catch { return false }
}

export function mosquitoNetSummary(value?: MosquitoNetConfiguration) {
  const net = normalizeMosquitoNet(value)
  if (net.type === 'roll') return `Roll · Spring: ${net.spring ? 'Yes' : 'No'}`
  if (net.type === 'slide') return `Slide · Fixed side: ${net.fixedSide}${net.netColorBlack ? ' · Black mesh' : ''}`
  if (net.type === 'pleated') return `Pleated · Stacked side: ${net.fixedSide}`
  return `Fixed${net.netColorBlack ? ' · Black mesh' : ''}`
}
